import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';


import { FreyaCommonModule } from '../../../../freya-common/freya-common.module';
import { JOB_ROLE_MAP, JobRole } from '../../../../global.constants';
import { PermissionService } from '../../../../services/permission.service';
import { SharedModule } from '../../../../shared/shared.module';
import { createConfigSelector } from '../../../../state/branding.store';

import {
  jobToolFeature,
  selectJob,
} from '../../../job-tool.reducer';

import { JobAgentActions } from './job-agent.actions';

const selectExceptionEmails = createSelector(
  createConfigSelector('JOBS_AUTOASSIGNAGENTEXCEPTIONEMAILS', ''),
  (emails) => String(emails ?? '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean),
);

const selectAgentsCanReassignSalesAgent = createConfigSelector('JOBS_AGENTSCANREASSIGNSALESAGENT', false);

@Component({
  selector: 'app-job-agent',
  standalone: true,
  imports: [
    FreyaCommonModule,
    SharedModule,
  ],
  templateUrl: './job-agent.component.html',
  styleUrl: './job-agent.component.scss',
})
export class JobAgentComponent implements OnInit {

  @Input() role: JobRole;

  @HostListener('document:click', ['$event'])
  clickedOut(event: MouseEvent) {

    const eventPath = event.composedPath();

    const isInnerClick = eventPath.includes(this.el.nativeElement);

    if (isInnerClick) { return;}

    const isMenuClick = eventPath
      .some((el) => el instanceof HTMLElement && el.classList.contains(this.classes.menu));

    if (isMenuClick) { return; }

    const isAutocompleteClick = eventPath
      .some((el) => el instanceof HTMLElement && el.classList.contains(this.classes.employeesPanel));

    if (isAutocompleteClick) { return; }

    this.store.dispatch(this.actions.clickedOutside({ role: this.role }));
  }

  public classes = {
    menu: 'agentActionsMenu',
    employeesPanel: 'employeesPanel',
  } as const;

  public actions = JobAgentActions;

  public actionMenuItems: MenuItem[] = [];

  private hasReassignPermission$ = this.permissionService.watchPermission('jobAgent.reassign');
  private exceptionEmails$ = this.store.select(selectExceptionEmails);
  private hasUpdateJobsPermission$ = this.permissionService.watchPermission('jobs.update');
  private agentsCanReassignSalesAgent$ = this.store.select(selectAgentsCanReassignSalesAgent);

  public canEditAgent$ = combineLatest([
    this.store.select(selectJob),
    this.hasReassignPermission$,
    this.exceptionEmails$,
    this.hasUpdateJobsPermission$,
    this.agentsCanReassignSalesAgent$,
  ]).pipe(
    map(([ job, hasReassignPermission, exceptionEmails, hasUpdateJobsPermission, agentsCanReassignSalesAgent ]) => {
      if (hasReassignPermission) { return true; }
      if (this.role === JOB_ROLE_MAP.salesAgentRole && hasUpdateJobsPermission && agentsCanReassignSalesAgent) {
        return true;
      }
      const agent = job?.users?.find((u) => u.role === this.role);
      const agentEmail = agent?.user?.email?.toLowerCase();
      return agentEmail !== undefined && exceptionEmails.includes(agentEmail);
    }),
  );

  constructor(
    public store: Store,
    private el: ElementRef,
    private permissionService: PermissionService,
  ) { }

  public viewModel$ = this.store.select(createSelector({
    employees: createSelector(jobToolFeature.selectEmployeeSuggestions, (employees) => {
      return employees.map((employee) => ({
        ...employee,
        name: [employee.givenName, employee.familyName].filter(Boolean).join(' '),
      }));
    }),
    readModeAgent: createSelector(
      selectJob,
      jobToolFeature.selectSelectedEmployees,
      jobToolFeature.selectAgentMutating,
      (job, selectedEmployees, jobAgentMutating ) => {

        // Optimistic UI update: show intended new agent if actively mutating agent, otherwise show current job agent
        const agent = jobAgentMutating ?
          selectedEmployees[this.role] : job?.users?.find((u) => u.role === this.role)?.user;

        if (!agent) { return; }

        return {
          ...agent,
          name: [agent.givenName, agent.familyName].filter(Boolean).join(' '),
        }
        },
    ),
    agent: createSelector(jobToolFeature.selectSelectedEmployees, (selectedEmployees) => {

      const agent = selectedEmployees[this.role];

      if (!agent) { return; }

      return {
        ...agent,
        name: [agent.givenName, agent.familyName].filter(Boolean).join(' '),
      }
    }),
    isEditMode: createSelector(jobToolFeature.selectIsEditRole, (isEditRole) => this.role in isEditRole),
    emptyMessage: createSelector(
      jobToolFeature.selectEmployeeSearch,
      jobToolFeature.selectSelectedEmployees,
      (employeeSearch, selectedEmployees) => {

        const hasSearch = this.role in employeeSearch && employeeSearch[this.role].length > 0;

        const hasSelectedEmployee = this.role in selectedEmployees;

        return hasSearch && !hasSelectedEmployee ? 'No Employee Found' : 'Search Employee';
      }
    ),
  }));

  ngOnInit() {
    this.actionMenuItems = [
      {
        label: `Change ${this.role}`,
        icon: 'pi pi-pencil',
        command: () => {
          this.store.dispatch(this.actions.changeAgentMenuItemClicked({ role: this.role }))
        },
      },
      {
        label: `Unassign ${this.role}`,
        icon: 'pi pi-times',
        command: () => {
          this.store.dispatch(this.actions.unassignAgentMenuItemClicked({ role: this.role }))
        },
      },
    ];
  }

}
